import log from 'loglevel'
import React from 'react'
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  Typography
} from '@mui/material'
import {
  Confirm,
  Datagrid,
  ListContextProvider,
  Pagination,
  TextField,
  useList,
  useNotify,
  useTranslate
} from 'react-admin'
import { isEmpty, isNil, map, split } from 'lodash/fp'
import {
  createMappingsXlsx,
  createSolarAccountsXlsx,
  doCognitoDownload,
  doDownload,
  doMappingsDownload,
  doSolarAccountsDownload
} from './functions'
import { type PartnerUser, type Partner } from '@mzc-watchover/adminapp-appsync'
import { format, parseISO } from 'date-fns/fp'
import { type ConfigProps } from 'components/Dashboard'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

export const DownloadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: Partial<Partner> | undefined
}> = ({ open, setOpen, partner }) => {
  const translate = useTranslate()
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    doDownload(partner)
      .then((_status: number | undefined) => {})
      .catch(log.error)
  }
  return (
    <Confirm
      isOpen={open}
      title={false}
      content={map((item: string) => (
        <Typography variant="body1" key={item}>
          {item}
          <br />
        </Typography>
      ))(split('\n')(translate('resources.download.dialog.content')))}
      onClose={onClose}
      onConfirm={onConfirm}
      cancel="resources.download.dialog.buttons.cancel"
      confirm="resources.download.dialog.buttons.confirm"
    />
  )
}
export const CognitoDownloadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
}> = ({ open, setOpen }) => {
  const translate = useTranslate()
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    doCognitoDownload()
      .then((_status: number | undefined) => {})
      .catch(log.error)
  }
  return (
    <Confirm
      isOpen={open}
      title={false}
      content={map((item: string) => (
        <Typography variant="body1" key={item}>
          {item}
          <br />
        </Typography>
      ))(split('\n')(translate('resources.cognito.download.dialog.content')))}
      onClose={onClose}
      onConfirm={onConfirm}
      cancel="resources.cognito.download.dialog.buttons.cancel"
      confirm="resources.cognito.download.dialog.buttons.confirm"
    />
  )
}
export const SolarAccountsDownloadDialog: React.FC<
  ConfigProps & {
    open: boolean
    setOpen: (open: boolean) => void
    partner: Partial<Partner>
  }
> = ({ open, setOpen, partner, config }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    notify('resources.cognito.download.notification.downloading', {
      autoHideDuration: null
    })
    doSolarAccountsDownload(partner)
      .then(({ mappings, accounts }) => {
        if (!isNil(accounts) && !isNil(mappings)) {
          createSolarAccountsXlsx(mappings, accounts, config)
            .then((xlsx) => {
              if (!isNil(xlsx)) {
                notify('resources.mappings.download.notification.downloading', {
                  autoHideDuration: 0
                })
                const blob = new Blob([xlsx], {
                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                })
                const url = URL.createObjectURL(blob)
                const download = document.createElement('a')
                download.href = url
                download.download = 'アカウント一覧.xlsx'
                download.click()
                window.URL.revokeObjectURL(url)
              } else {
                notify('resources.cognito.download.notification.downloadError')
              }
            })
            .catch((error) => {
              log.error('[functions.SolarAccountsDownloadDialog.onConfirm]', {
                error
              })
              notify('resources.cognito.download.notification.downloadError')
            })
        }
      })
      .catch((error) => {
        log.error('[functions.SolarAccountsDownloadDialog.onConfirm]', {
          error
        })
      })
      .finally(() => {
        setOpen(false)
      })
  }
  return (
    <Confirm
      isOpen={open}
      title={false}
      content={map((item: string) => (
        <Typography variant="body1" key={item}>
          {item}
          <br />
        </Typography>
      ))(split('\n')(translate('resources.cognito.download.dialog.content')))}
      onClose={onClose}
      onConfirm={onConfirm}
      cancel="resources.cognito.download.dialog.buttons.cancel"
      confirm="resources.cognito.download.dialog.buttons.confirm"
    />
  )
}
export const MappingsDownloadDialog: React.FC<
  ConfigProps & {
    open: boolean
    setOpen: (open: boolean) => void
    partner: Partial<Partner>
  }
> = ({ open, setOpen, partner, config }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    notify('resources.mappings.download.notification.downloading', {
      autoHideDuration: null
    })
    doMappingsDownload(partner)
      .then(({ mappings, accounts, ownerAccounts }) => {
        if (!isNil(mappings) && !isNil(accounts)) {
          if (isEmpty(accounts)) {
            notify('resources.mappings.download.notification.noAccounts')
          }
          createMappingsXlsx(mappings, accounts, ownerAccounts ?? [], config)
            .then((xlsx) => {
              if (!isNil(xlsx)) {
                notify('resources.mappings.download.notification.downloading', {
                  autoHideDuration: 0
                })
                const blob = new Blob([xlsx], {
                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                })
                const url = URL.createObjectURL(blob)
                const download = document.createElement('a')
                download.href = url
                download.download = '拠点一覧.xlsx'
                download.click()
                window.URL.revokeObjectURL(url)
              } else {
                notify('resources.mappings.download.notification.downloadError')
              }
            })
            .catch((error) => {
              log.error('[MappingsDownloadDialog.onConfirm]', { error })
              notify('resources.mappings.download.notification.downloadError')
            })
        }
      })
      .catch((error) => {
        log.error('[MappingsDownloadDialog.onCofirm]', { error })
      })
      .finally(() => {
        setOpen(false)
      })
  }
  return (
    <Confirm
      isOpen={open}
      title={false}
      content={map((item: string) => (
        <Typography variant="body1" key={item}>
          {item}
          <br />
        </Typography>
      ))(split('\n')(translate('resources.cognito.download.dialog.content')))}
      onClose={onClose}
      onConfirm={onConfirm}
      cancel="resources.cognito.download.dialog.buttons.cancel"
      confirm="resources.cognito.download.dialog.buttons.confirm"
    />
  )
}
const MissingsList: React.FC<{
  missings: Array<Partial<PartnerUser> & { from: string }>
}> = ({ missings }) => {
  const data = map((item: Partial<PartnerUser> & { from: string }) => {
    const { name, from, id } = item
    return {
      name,
      from: format('yyyy年M月d日')(parseISO(from)),
      id: id as string
    }
  })(missings)
  const listContext = useList({ data, perPage: 10 })
  return (
    <ListContextProvider value={listContext}>
      <Datagrid bulkActionButtons={false}>
        <TextField source="name" label="resources.missingsDialog.list.name" />
        <TextField source="from" label="resources.missingsDialog.list.from" />
      </Datagrid>
      <Pagination />
    </ListContextProvider>
  )
}
export const MissingsDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  disabled: boolean
  doDownload: () => void
  missings: Array<Partial<PartnerUser> & { from: string }>
}> = ({ open, setOpen, disabled, doDownload, missings }) => {
  const translate = useTranslate()
  const handleClose = (): void => {
    setOpen(false)
  }
  const onClick = (): void => {
    setOpen(false)
  }
  const onClickExec = (): void => {
    setOpen(false)
    doDownload()
  }
  return (
    <>
      <Dialog onClose={handleClose} open={open} maxWidth="lg" fullWidth>
        <DialogContent>
          <Card>
            <CardContent>
              <Typography variant="body1" sx={{ mt: 2 }}>
                {translate('resources.missingsDialog.content')}
              </Typography>
              <MissingsList missings={missings} />
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={onClickExec} disabled={disabled}>
            {translate('resources.missingsDialog.buttons.exec')}
          </Button>
          <Button variant="outlined" onClick={onClick}>
            {translate('resources.missingsDialog.buttons.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
